import {ApiRepository, ValidationError} from '../base/api-repository';
import {action, makeObservable, observable} from 'mobx';
import {Language} from '../base/label-store';
import {SessionStore} from './session-store';
import * as Sentry from '@sentry/react';
import {formatToYearMonthAndDayWithDateInput, isDateInFuture} from '../base/dateUtils';
import {DateRange} from 'react-day-picker';
import {BusinessUnit} from '../company/company-page-store';
import {Announcement} from '../questions/questions-store';

type State = 'no-user' | 'loading-user' | 'user-loaded' | 'error';
type ErrorReason = 'user-deleted' | 'generic';
export class UserStore {
    @observable
    payload?: UserPayload = undefined;
    @observable
    isCalendarOpen: boolean = false;
    @observable
    state: State = 'no-user';
    @observable
    errorReason: ErrorReason = 'generic';

    constructor(private api: ApiRepository, private sessionStore: SessionStore) {
        makeObservable(this);
    }

    @action
    async loadSsoUser(tenantId: string): Promise<void> {
        const success = await this.sessionStore.extendSsoSession(tenantId);

        if (success) {
            await this.loadUser();
        }
    }

    @action
    async loadUser(): Promise<void> {
        this.setState('loading-user');
        const employeeId = this.sessionStore.getEmployeeId();

        try {
            this.payload = await this.api.get<UserPayload>(`/user/${employeeId}`);
            this.setState('user-loaded');
            this.isCalendarOpen = this.payload.vacationTime !== undefined && !isDateInFuture(new Date(this.payload.vacationTime.from!));
            Sentry.setUser({id: this.payload.employeeId});
        } catch (e: unknown) {
            if ((e as ValidationError).name === 'ForbiddenError' && (e as ValidationError).errorMessageCode === 'user-deleted') {
                this.setErrorReason('user-deleted');
                this.setState('error');
            } else {
                console.error(e);
                this.setState('error');
                this.setErrorReason('generic');
                Sentry.captureException(e);
            }
        }
    }

    @action
    async refreshUser(): Promise<void> {
        try {
            this.payload = await this.api.get<UserPayload>(`/user/${this.payload!.employeeId}`);
            this.isCalendarOpen = this.payload.vacationTime !== undefined && !isDateInFuture(new Date(this.payload.vacationTime.from!));
        } catch (e: unknown) {
            if ((e as ValidationError).name === 'ForbiddenError' && (e as ValidationError).errorMessageCode === 'user-deleted') {
                this.setErrorReason('user-deleted');
                this.setState('error');
            } else {
                console.error(e);
                this.setState('error');
                this.setErrorReason('generic');
                Sentry.captureException(e);
            }
        }
    }

    @action
    async changeLanguage(language: Language, employeeId?: string): Promise<void> {
        try {
            this.payload = await this.api.post<UserPayload>(`/user/${employeeId !== undefined ? employeeId : this.payload!.employeeId}/language/${language}`);
        } catch (e: unknown) {
            console.error(e);
            this.setState('error');
            Sentry.captureException(e);
        }
    }

    @action
    async changeDeliveryTime(deliveryTime: DeliveryTime, employeeId?: string): Promise<void> {
        try {
            this.payload = await this.api.post<UserPayload>(`/user/${employeeId !== undefined ? employeeId : this.payload!.employeeId}/delivery-time/${deliveryTime}`);
        } catch (e: unknown) {
            console.error(e);
            this.setState('error');
            Sentry.captureException(e);
        }
    }

    @action
    async changeQuestionsFrequency(frequency: QuestionsFrequency, employeeId?: string): Promise<void> {
        try {
            this.payload = await this.api.post<UserPayload>(`/user/${employeeId !== undefined ? employeeId : this.payload!.employeeId}/questions-frequency/${frequency}`);
        } catch (e: unknown) {
            console.error(e);
            this.setState('error');
            Sentry.captureException(e);
        }
    }

    @action
    async changeAnswerContext(scale: AnswerContext, employeeId?: string): Promise<void> {
        try {
            this.payload = await this.api.post<UserPayload>(`/user/${employeeId !== undefined ? employeeId : this.payload!.employeeId}/answer-context/${scale}`);
        } catch (e: unknown) {
            console.error(e);
            this.setState('error');
            Sentry.captureException(e);
        }
    }

    @action
    async initiateChannelChange(channel: Channel): Promise <void> {
        const isEmployeeMobileUser = this.payload?.hasMobileAppInstalled !== undefined && this.payload?.hasMobileAppInstalled === true;

        if (channel === 'mobile' && !isEmployeeMobileUser) {
            window.open('https://moticheck.com/app/');
        } else {
            await this.changeChannel(channel);
        }
    }

    @action
    async changeChannel(channel: Channel, employeeId?: string): Promise<void> {
        try {
            this.payload = await this.api.post<UserPayload>(`/user/${employeeId !== undefined ? employeeId : this.payload!.employeeId}/channel/${channel}`);
        } catch (e: unknown) {
            console.error(e);
            this.setState('error');
            Sentry.captureException(e);
        }
    }

    @action
    async changeTimeZone(timeZone: string, employeeId?: string): Promise<void> {
        try {
            this.payload = await this.api.post<UserPayload>(
                `/user/${employeeId !== undefined ? employeeId : this.payload!.employeeId}/timezone/${encodeURIComponent(timeZone)}`,
            );
        } catch (e: unknown) {
            console.error(e);
            this.setState('error');
            Sentry.captureException(e);
        }
    }

    @action
    async submitOnboardingConsent(onboardingConsent: OnboardingConsent, employeeId?: string): Promise<void> {
        try {
            this.payload = await this.api.post<UserPayload>(`/user/${employeeId !== undefined ? employeeId : this.payload!.employeeId}/onboarding-consent/${onboardingConsent}`);
        } catch (e: unknown) {
            console.error(e);
            this.setState('error');
            Sentry.captureException(e);
        }
    }

    @action
    openVacationCalendar(): void {
        this.isCalendarOpen = true;
    }

    @action
    async scheduleVacation(range: DateRange | undefined): Promise<void> {
        if (range?.to === null || range?.from === null || range?.to === undefined || range?.from === undefined) {
            return;
        }
        let validatedRange: DateRange = range;

        if (range.from > range.to) {
            validatedRange = { from: range.to, to: range.from };
        }
        const formattedRange = {
            from: formatToYearMonthAndDayWithDateInput(validatedRange.from),
            to: formatToYearMonthAndDayWithDateInput(validatedRange.to),
        };

        try {
            const employeeId = this.sessionStore.getEmployeeId();

            this.payload = await this.api.post<UserPayload>(`/user/${employeeId}/book-vacation`, formattedRange);
            this.isCalendarOpen = this.payload.vacationTime !== undefined && !isDateInFuture(new Date(this.payload.vacationTime.from!));
        } catch (e: unknown) {
            console.error(e);
            this.setState('error');
            Sentry.captureException(e);
        }
    }


    @action
    async cancelScheduledVacation(): Promise<void> {
        const vacationId = this.payload?.vacationTimeId;

        if (vacationId === undefined) {
            return;
        } else {
            try {
                this.isCalendarOpen = false;
                const employeeId = this.sessionStore.getEmployeeId();

                this.payload = await this.api.post<UserPayload>(`/user/${employeeId}/cancel-vacation/${vacationId}`);
            } catch (e: unknown) {
                console.error(e);
                this.setState('error');
                Sentry.captureException(e);
            }
        }
    }
    @action
    async dismissAnnouncement(announcementId: string): Promise<void> {
        const employeeId = this.sessionStore.getEmployeeId();

        if (!this.payload || !employeeId) {
            return;
        }

        try {
            await this.api.post(`/announcements/employee/${employeeId}/dismiss`, {
                id: announcementId,
            });
            this.payload.announcements = this.payload.announcements.filter((a) => a.id !== announcementId);
        } catch (e: unknown) {
            console.error(e);
            this.setState('error');
            Sentry.captureException(e);
        }
    }

    @action
    clear(): void {
        this.payload = undefined;
        this.setState('no-user');
        Sentry.setUser(null);
    }

    @action
    private setState(state: State): void {
        this.state = state;
    }

    @action
    private setErrorReason(errorReason: ErrorReason): void {
        this.errorReason = errorReason;
    }
}

export type DeliveryTime = 'morning' | 'afternoon' | 'evening' | 'latenight';
export type Channel = 'email' | 'mobile' | 'slack' | 'teams';
export type OnboardingConsent = 'yes' | 'no';
export type QuestionsFrequency = 'one_every_day' | 'one_on_even_dates' | 'weekly';
export type AnswerContext = 'BINARY' | 'FOUR_OPTIONS' | 'FIVE_OPTIONS' | 'ONLY_FREETEXT' | 'ONE_TO_FIVE_OPTIONS';

interface UserPayload {
    employeeId: string;
    fullName: EmployeeName;
    businessUnitName: string;
    businessUnitId: string;
    isManagerOrPartner: boolean;
    isAdmin: boolean;
    isManager: boolean;
    companyId: string;
    language: Language;
    deliveryTime: DeliveryTime;
    employeeQuestionsFrequency: QuestionsFrequency;
    companyQuestionsFrequency: QuestionsFrequency;
    channel: Channel;
    eligibleChannelsForUserMenu: Channel[];
    hasMobileAppInstalled: boolean;
    companyLanguages: Language[];
    peopleInOnboardingCount?: number;
    vacationTime?: Period;
    vacationTimeId?: string;
    companyAnswerContext: AnswerContext[];
    employeeAnswerContext: AnswerContext;
    isMentorEnabled: boolean;
    isPersonalDashboardEnabled: boolean;
    logoFile: string;
    isOnboarding: boolean;
    timezoneId: string[],
    employeeTimeZone: string,
    businessUnits: BusinessUnit[],
    partnerForBusinessUnits: string[],
    isBusinessUnitDisablingEnabled: boolean,
    announcements: Announcement[],
}

export interface Period {
    from: string;
    to: string;
}

interface EmployeeName {
    firstName: string,
    lastName: string
}
